'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { ContextNavigation, ListPageSearch } from '@terra/ui-design-system';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import type { SignalStageType } from '@terra/shared-schemas-orchestrator';
import type { SignalCountsResult } from '../../../hooks/useSignalCounts';
import { ListPageRefreshButton } from '../../common/ListPageRefreshButton';
import {
  SIGNALS_STAGE_CHIP_ALL,
  SIGNALS_STAGE_CHIPS,
  resolveSignalsStageChip,
  resolveSignalsStageChipCount,
  toSignalsStageFilter,
  type SignalsStageChip,
} from '../signals-table/signals-stage-chips.model';

const LIFECYCLE_VIEW_CHIPS = ['all', 'active', 'archived'] as const;

type LifecycleViewChip = (typeof LIFECYCLE_VIEW_CHIPS)[number];

type SignalsContextNavigationProps = {
  isStageChipsEnabled: boolean;
  showLifecycleViews: boolean;
  lifecycleState: LifecycleStateFilterDto;
  onLifecycleStateChange: (lifecycleState: LifecycleStateFilterDto) => void;
  lifecycleCounts: Partial<Record<LifecycleViewChip, number>> | undefined;
  stageFilter: SignalStageType[];
  onStageFilterChange: (stageFilter: SignalStageType[]) => void;
  stageCounts: SignalCountsResult | undefined;
  searchTerm: string;
  onSearchTermChange: (searchTerm: string) => void;
  onRefresh: () => void;
  isLoading: boolean;
  optionsMenu: ReactNode;
};

function lifecycleViewChipLabel({
  chip,
  labels,
}: {
  chip: LifecycleViewChip;
  labels: Record<LifecycleViewChip, string>;
}): string {
  switch (chip) {
    case 'all':
    case 'active':
    case 'archived':
      return labels[chip];
    default: {
      const _exhaustive: never = chip;

      return _exhaustive;
    }
  }
}

export function SignalsContextNavigation({
  isStageChipsEnabled,
  showLifecycleViews,
  lifecycleState,
  onLifecycleStateChange,
  lifecycleCounts,
  stageFilter,
  onStageFilterChange,
  stageCounts,
  searchTerm,
  onSearchTermChange,
  onRefresh,
  isLoading,
  optionsMenu,
}: SignalsContextNavigationProps) {
  const tCommon = useTranslations('designSystem.common');
  const tListPage = useTranslations('designSystem.listPage');
  const tStage = useTranslations('designSystem.signals.lifecycle.stage');
  const tSignalEntity = useTranslations('designSystem.signals.drawer');
  const lifecycleChipLabels = useMemo(
    (): Record<LifecycleViewChip, string> => ({
      all: tCommon('all'),
      active: tCommon('active'),
      archived: tCommon('archived'),
    }),
    [tCommon],
  );
  const lifecycleItems = useMemo(
    () =>
      LIFECYCLE_VIEW_CHIPS.map((chip) => ({
        value: chip,
        label: lifecycleViewChipLabel({ chip, labels: lifecycleChipLabels }),
        count: lifecycleCounts ? (lifecycleCounts[chip] ?? 0) : undefined,
      })),
    [lifecycleChipLabels, lifecycleCounts],
  );
  const stageItems = useMemo(
    () =>
      isStageChipsEnabled
        ? SIGNALS_STAGE_CHIPS.map((chip) => ({
            value: chip,
            label:
              chip === SIGNALS_STAGE_CHIP_ALL ? tCommon('all') : tStage(chip),
            count: resolveSignalsStageChipCount({ chip, stageCounts }),
          }))
        : [],
    [isStageChipsEnabled, stageCounts, tCommon, tStage],
  );
  const actions = (
    <>
      <ListPageSearch
        value={searchTerm}
        onValueChange={onSearchTermChange}
        placeholder={tCommon('searchByNameOrId', {
          entityName: tSignalEntity('title'),
        })}
      />
      <ListPageRefreshButton onRefresh={onRefresh} isLoading={isLoading} />
      {optionsMenu}
    </>
  );

  if (showLifecycleViews) {
    return (
      <ContextNavigation<LifecycleViewChip>
        ariaLabel={tListPage('lifecycle')}
        items={lifecycleItems}
        value={lifecycleState}
        onValueChange={onLifecycleStateChange}
        actions={actions}
      />
    );
  }

  return (
    <ContextNavigation<SignalsStageChip>
      ariaLabel={tStage('label')}
      items={stageItems}
      value={resolveSignalsStageChip(stageFilter)}
      onValueChange={(chip) => onStageFilterChange(toSignalsStageFilter(chip))}
      actions={actions}
    />
  );
}
